import { useState, useEffect } from "react";
import {
  Send,
  Loader,
  Check,
  DollarSign,
  FileText,
  Mail,
  User,
  Building,
  Briefcase,
  Globe,
  Link,
  MapPin,
  TrendingUp,
  MessageCircle,
} from "lucide-react";
import InlineEditField from "./InlineEditField";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Typography,
  Button,
} from "@mui/material";
import "./Modal.css";

const ContractModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  showSuccess,
  successMessage,
  contactDetails,
  contactId,
  portalId,
}) => {
  const [formData, setFormData] = useState({
    projectTitle: "",
    engagementType: "",
    duration: "",
    firstPaymentAmount: "",
    amount: "",
    solutionsServices: "",
    pricingTerms: "",
    customPricingValue: "",
    emailForContract: "",
    emailForInvoicing: "",
    numberOfResources: "",
    assignmentType: "",
    additionalComments: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [showValidationSummary, setShowValidationSummary] = useState(false);

  // Checkbox states for conditional fields
  const [fieldEnabled, setFieldEnabled] = useState({
    emailForContract: true,
    emailForInvoicing: true,
  });

  // Contact data state - using exact keys from API response
  const [contactData, setContactData] = useState({
    address: "",
    campaign: "",
    category: "",
    city: "",
    company: "",
    country: "",
    createdate: "",
    email: "",
    engagement_type: "",
    firstname: "",
    hs_object_id: "",
    hubspot_owner_id: "",
    industry: "",
    jobtitle: "",
    lastmodifieddate: "",
    lastname: "",
    linkedin_profile: "",
    linkedinbio: "",
    number_of_linkedin_employees: "",
    phone: "",
    solutions: "",
    state: "",
    technology_stack: "",
    website: "",
    zip: "",
    linkedin_description: "",
    founded_year: "",
    description: "",
    hs_keywords: "",
    company_specialities: "",
    hubspotContactUrl: "",
  });

  const [loadingContact, setLoadingContact] = useState(false);
  const [contactError, setContactError] = useState("");

  // Deal selection state
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState("");
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [dealError, setDealError] = useState("");
  const [dealStage, setDealStage] = useState("contractsent"); // Default to "Contract Sent" for Contract Modal

  // Form validation rules
  // Get list of all required fields (excluding additionalComments)
  const getRequiredFields = () => {
    const allFields = Object.keys(formData);
    const contactFields = [
      "clientName",
      "companyName",
      "companyWebsite",
      "emailAddress",
      "clientLocation",
    ];
    const dealFields = ["selectedDeal", "dealStage"];

    // Combine all field types
    const requiredFields = [];

    // Add form data fields
    allFields.forEach((field) => {
      // Always exclude additionalComments
      if (field === "additionalComments") return;

      // Conditionally exclude fields based on engagement type
      if (
        formData.engagementType === "Project" &&
        (field === "numberOfResources" || field === "assignmentType")
      ) {
        return;
      }

      // Conditionally exclude email fields if disabled
      if (field === "emailForContract" && !fieldEnabled.emailForContract)
        return;
      if (field === "emailForInvoicing" && !fieldEnabled.emailForInvoicing)
        return;

      // Conditionally exclude customPricingValue if not using Custom Payment Terms
      if (
        field === "customPricingValue" &&
        formData.pricingTerms !== "Custom Payment Terms"
      )
        return;

      requiredFields.push(field);
    });

    // Add contact fields
    requiredFields.push(...contactFields);

    // Add deal selection field
    requiredFields.push(...dealFields);

    return requiredFields;
  };

  // Generate validation summary
  const getValidationSummary = () => {
    const requiredFields = getRequiredFields();
    const fieldLabels = {
      // Form fields
      projectTitle: "Project Title",
      solutionsServices: "Solutions/Services",
      engagementType: "Engagement Type",
      duration: "Contract Duration",
      firstPaymentAmount: "First Payment Amount",
      amount: "Total Project Cost",
      numberOfResources: "Number of Resources",
      assignmentType: "Assignment Type",
      pricingTerms: "Payment Terms",
      customPricingValue: "Custom Payment Terms",
      emailForContract: "Contract Email",
      emailForInvoicing: "Invoicing Email",
      // Contact fields
      clientName: "Client Name",
      companyName: "Company Name",
      companyWebsite: "Company Website",
      emailAddress: "Email Address",
      clientLocation: "Client Location",
      // Deal selection
      selectedDeal: "Deal Selection",
      dealStage: "Deal Stage",
    };

    const summary = {
      total: requiredFields.length,
      filled: 0,
      missing: [],
      completed: [],
    };

    requiredFields.forEach((field) => {
      let value;
      let isFilled = false;

      // Handle different data sources
      if (field === "selectedDeal") {
        value = selectedDealId;
        isFilled = value && value.toString().trim() !== "";
      } else if (field === "dealStage") {
        value = dealStage;
        isFilled = value && value.toString().trim() !== "";
      } else if (field === "clientName") {
        value = `${contactData.firstname || ""} ${
          contactData.lastname || ""
        }`.trim();
        // Consider filled if we have either first name OR last name (not both required)
        isFilled =
          (contactData.firstname && contactData.firstname.trim() !== "") ||
          (contactData.lastname && contactData.lastname.trim() !== "");
      } else if (field === "companyName") {
        value = contactData.company;
        isFilled = value && value.toString().trim() !== "";
      } else if (field === "companyWebsite") {
        value = contactData.website;
        isFilled = value && value.toString().trim() !== "";
      } else if (field === "emailAddress") {
        value = contactData.email;
        isFilled = value && value.toString().trim() !== "";
      } else if (field === "clientLocation") {
        const locationParts = [
          contactData.city,
          contactData.state,
          contactData.country,
        ].filter(Boolean);
        value = locationParts.join(", ");
        isFilled = locationParts.length > 0;
      } else {
        // Regular form fields
        value = formData[field];
        isFilled = value && value.toString().trim() !== "";
      }

      if (isFilled) {
        summary.filled++;
        summary.completed.push(fieldLabels[field] || field);
      } else {
        summary.missing.push(fieldLabels[field] || field);
      }
    });

    return summary;
  };

  const validateField = (name, value) => {
    switch (name) {
      case "projectTitle":
        if (!value.trim()) return "Project title is required";
        if (value.length < 3)
          return "Project title must be at least 3 characters";
        return "";

      case "solutionsServices":
        if (!value.trim()) return "Solutions/Services description is required";
        if (value.length < 10)
          return "Please provide more detailed description";
        return "";

      case "engagementType":
        if (!value) return "Engagement type is required";
        return "";

      case "duration":
        if (!value) return "Contract duration is required";
        return "";

      case "firstPaymentAmount":
        if (!value) return "First payment amount is required";
        if (isNaN(value) || parseFloat(value) <= 0)
          return "Please enter a valid amount";
        if (parseFloat(value) < 100) return "Minimum amount is $100";
        return "";

      case "amount":
        if (!value) return "Total project cost is required";
        if (isNaN(value) || parseFloat(value) <= 0)
          return "Please enter a valid amount";
        return "";

      case "numberOfResources":
        // Only validate if engagement type is not "Project"
        if (formData.engagementType !== "Project" && !value) {
          return "Number of resources is required";
        }
        return "";

      case "assignmentType":
        // Only validate if engagement type is not "Project"
        if (formData.engagementType !== "Project" && !value) {
          return "Assignment type is required";
        }
        return "";

      case "pricingTerms":
        if (!value || !value.trim()) return "Payment terms are required";
        return "";

      case "emailForContract":
        // Only validate if field is enabled
        if (!fieldEnabled.emailForContract) return "";
        if (!value.trim()) return "Contract email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value))
          return "Please enter a valid email address";
        return "";

      case "emailForInvoicing":
        // Only validate if field is enabled
        if (!fieldEnabled.emailForInvoicing) return "";
        if (!value.trim()) return "Invoicing email is required";
        const invoiceEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!invoiceEmailRegex.test(value))
          return "Please enter a valid email address";
        return "";

      case "customPricingValue":
        // Only validate if Custom Payment Terms is selected
        if (formData.pricingTerms === "Custom Payment Terms" && !value.trim()) {
          return "Custom payment terms are required";
        }
        return "";

      // Contact field validations
      case "clientName":
        // Allow form to be valid if we have either first name OR last name (not both required)
        const hasFirstName =
          contactData.firstname && contactData.firstname.trim() !== "";
        const hasLastName =
          contactData.lastname && contactData.lastname.trim() !== "";
        if (!hasFirstName && !hasLastName) return "Client name is required";
        return "";

      case "companyName":
        if (!contactData.company || !contactData.company.trim())
          return "Company name is required";
        return "";

      // case "companyWebsite":
      //   if (!contactData.website || !contactData.website.trim())
      //     return "Company website is required";
      //   // Very basic URL validation - just check for domain.extension format
      //   // const urlPattern = /\w+\.\w+/;
      //   const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
      //   if (!urlPattern.test(contactData.website))
      //     return "Please enter a valid website (e.g., test.com)";
      //   return "";

      case "companyWebsite":
        if (!contactData.website || !contactData.website.trim())
          return "Company website is required";
        return "";

      case "emailAddress":
        if (!contactData.email || !contactData.email.trim())
          return "Email address is required";
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(contactData.email))
          return "Please enter a valid email address";
        return "";

      case "clientLocation":
        const locationParts = [
          contactData.city,
          contactData.state,
          contactData.country,
        ].filter(Boolean);
        if (locationParts.length === 0) return "Client location is required";
        return "";

      // Deal selection validation
      case "selectedDeal":
        if (!selectedDealId || selectedDealId.trim() === "")
          return "Please select a deal";
        return "";

      case "dealStage":
        if (!dealStage || dealStage.trim() === "")
          return "Please select a deal stage";
        return "";

      default:
        return "";
    }
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = getRequiredFields();
    const emptyFields = [];

    console.log("🔍 VALIDATION DEBUG - Required fields:", requiredFields);
    console.log("🔍 VALIDATION DEBUG - Form data:", formData);
    console.log("🔍 VALIDATION DEBUG - Contact data:", contactData);
    console.log("🔍 VALIDATION DEBUG - Selected Deal ID:", selectedDealId);
    console.log("🔍 VALIDATION DEBUG - Deal Stage:", dealStage);
    console.log("🔍 VALIDATION DEBUG - Field Enabled:", fieldEnabled);

    requiredFields.forEach((key) => {
      let value;
      let isEmpty = false;

      // Handle different data sources like in getValidationSummary
      if (key === "selectedDeal") {
        value = selectedDealId;
        isEmpty = !value || value.toString().trim() === "";
      } else if (key === "dealStage") {
        value = dealStage;
        isEmpty = !value || value.toString().trim() === "";
      } else if (key === "clientName") {
        // Use the same logic as in getValidationSummary - check if we have either first or last name
        const hasFirstName =
          contactData.firstname && contactData.firstname.trim() !== "";
        const hasLastName =
          contactData.lastname && contactData.lastname.trim() !== "";
        value = hasFirstName || hasLastName ? "valid" : "";
        isEmpty = !hasFirstName && !hasLastName;
      } else if (key === "companyName") {
        value = contactData.company;
        isEmpty = !value || value.toString().trim() === "";
      } else if (key === "companyWebsite") {
        value = contactData.website;
        isEmpty = !value || value.toString().trim() === "";
      } else if (key === "emailAddress") {
        value = contactData.email;
        isEmpty = !value || value.toString().trim() === "";
      } else if (key === "clientLocation") {
        const locationParts = [
          contactData.city,
          contactData.state,
          contactData.country,
        ].filter(Boolean);
        value = locationParts.join(", ");
        isEmpty = locationParts.length === 0;
      } else {
        // Regular form fields
        value = formData[key];
        isEmpty = !value || value.toString().trim() === "";
      }

      // Log each field's status
      if (isEmpty) {
        console.log(`❌ EMPTY FIELD: ${key} = "${value}"`);
        emptyFields.push(key);
      } else {
        console.log(`✅ FILLED FIELD: ${key} = "${value}"`);
      }

      const error = validateField(key, value);
      if (error) newErrors[key] = error;
    });

    console.log("🚨 EMPTY FIELDS CAUSING VALIDATION TO FAIL:", emptyFields);
    console.log("🔥 VALIDATION ERRORS:", newErrors);
    console.log("🎯 FORM IS VALID:", Object.keys(newErrors).length === 0);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check form validity on data change
  useEffect(() => {
    const isValid = validateForm();
    setIsFormValid(isValid);
  }, [formData, contactData, selectedDealId, dealStage, fieldEnabled]);

  // Fetch contact data when component mounts or contactId changes
  useEffect(() => {
    if (contactId) {
      fetchContactData(contactId);
      fetchDeals(contactId);
    }
    // Always fetch deal stages and dynamic properties when modal opens
    fetchDealStages();
    fetchDynamicProperties();
  }, [contactId]);

  // Updated fetchContactData function
  const fetchContactData = async (id) => {
    setLoadingContact(true);
    setContactError("");

    try {
      // Get API URL from environment variable
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      console.log(
        "DEBUG ContractModal: REACT_APP_API_URL from env:",
        process.env.REACT_APP_API_URL
      );
      console.log("DEBUG ContractModal: Using apiUrl:", apiUrl);
      console.log(
        `DEBUG ContractModal: Full fetch URL: ${apiUrl}/api/hubspot/contact/${id}`
      );

      const response = await fetch(`${apiUrl}/api/hubspot/contact/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "omit",
        mode: "cors",
      });

      const data = await response.json();
      console.log("🔧 DEBUG ContractModal: Full API response:", data);

      if (data.success && data.contact) {
        // Directly map the API response to contactData state (using exact keys)
        setContactData({
          ...data.contact,
          hubspotContactUrl: data.metadata?.hubspotContactUrl || "",
        });

        // Pre-fill some form fields based on contact data
        const clientName = `${data.contact.firstname || ""} ${
          data.contact.lastname || ""
        }`.trim();
        const projectTitleBase = data.contact.company || clientName || "Client";

        setFormData((prev) => ({
          ...prev,
          projectTitle: ``,
          solutionsServices: data.contact.description,
          emailForContract: data.contact.email || "",
          emailForInvoicing: data.contact.email || "",
        }));

        console.log("Contact data loaded successfully:", data.contact);
      } else {
        // Handle error
        console.error("API error:", data.message);
        setContactError(data.message || "Failed to fetch contact data");
      }
    } catch (error) {
      console.error("Error fetching contact data:", error);
      setContactError("Error fetching contact data: " + error.message);
    } finally {
      setLoadingContact(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedFormData = { ...formData, [name]: value };

    // Clear numberOfResources and assignmentType when engagement type is "Project"
    if (name === "engagementType" && value === "Project") {
      updatedFormData.numberOfResources = "";
      updatedFormData.assignmentType = "";
    }

    setFormData(updatedFormData);

    // Mark field as touched
    setTouched({
      ...touched,
      [name]: true,
    });

    // Validate field on change
    const error = validateField(name, value);
    setErrors({
      ...errors,
      [name]: error,
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Auto-append company name to project title when user finishes typing
    if (name === "projectTitle" && contactData.company && value.trim()) {
      const companySuffix = ` - ${contactData.company}`;
      // Only append if the value doesn't already contain the company suffix
      if (!value.includes(companySuffix)) {
        const updatedFormData = {
          ...formData,
          projectTitle: value + companySuffix,
        };
        setFormData(updatedFormData);
      }
    }

    setTouched({
      ...touched,
      [name]: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Get validation summary
    const validationSummary = getValidationSummary();

    // If there are missing required fields, show validation summary
    if (validationSummary.missing.length > 0) {
      setShowValidationSummary(true);
      return;
    }

    // Final validation for detailed errors
    if (!validateForm()) {
      setShowValidationSummary(true);
      return;
    }

    // Hide validation summary if form is valid
    setShowValidationSummary(false);

    try {
      // DISABLED: Update contact information first if any fields were modified
      // await updateContactData();

      // Use the selected deal stage from the dropdown
      const submissionData = {
        modalType: "CONTRACT", // Explicitly specify this is a Contract Modal update
        ...formData,
        // Add the fields that will be used in both resource and contract forms
        contract_duration: formData.duration,
        services: formData.solutionsServices,
        pricing_terms: formData.pricingTerms, // Map pricingTerms to pricing_terms for backend
        // Use the deal stage selected in the dropdown
        dealStage: dealStage, // Use the current dealStage from the dropdown
        contactId: contactId,
        selectedDealId: selectedDealId,
        updateExistingDeal: selectedDeal ? true : false,
        // Include contact data for email/note generation (READ-ONLY - no updates to HubSpot contact)
        contactData: {
          clientName: `${contactData.firstname || ""} ${
            contactData.lastname || ""
          }`.trim(),
          companyName: contactData.company || "",
          companyWebsite: contactData.website || "",
          emailAddress: contactData.email || "",
          clientLocation: [
            contactData.city,
            contactData.state,
            contactData.country,
          ]
            .filter(Boolean)
            .join(", "),
          phone: contactData.phone || "",
          jobTitle: contactData.jobtitle || "",
          industry: contactData.industry || "",
          // Include all contact data for comprehensive email/note content
          fullContactData: contactData,
        },
      };

      // Only include email fields if they are enabled
      if (!fieldEnabled.emailForContract) {
        delete submissionData.emailForContract;
      }
      if (!fieldEnabled.emailForInvoicing) {
        delete submissionData.emailForInvoicing;
      }

      onSubmit(submissionData);
    } catch (error) {
      console.error("Error updating contact data:", error);
      // Continue with form submission even if contact update fails
      const submissionData = {
        modalType: "CONTRACT", // Explicitly specify this is a Contract Modal update
        ...formData,
        contract_duration: formData.duration,
        services: formData.solutionsServices,
        pricing_terms: formData.pricingTerms,
        dealStage: dealStage,
        contactId: contactId,
        selectedDealId: selectedDealId,
        updateExistingDeal: selectedDeal ? true : false,
        // Include contact data for email/note generation (READ-ONLY - no updates to HubSpot contact)
        contactData: {
          clientName: `${contactData.firstname || ""} ${
            contactData.lastname || ""
          }`.trim(),
          companyName: contactData.company || "",
          companyWebsite: contactData.website || "",
          emailAddress: contactData.email || "",
          clientLocation: [
            contactData.city,
            contactData.state,
            contactData.country,
          ]
            .filter(Boolean)
            .join(", "),
          phone: contactData.phone || "",
          jobTitle: contactData.jobtitle || "",
          industry: contactData.industry || "",
          // Include all contact data for comprehensive email/note content
          fullContactData: contactData,
        },
      };

      // Only include email fields if they are enabled (error path)
      if (!fieldEnabled.emailForContract) {
        delete submissionData.emailForContract;
      }
      if (!fieldEnabled.emailForInvoicing) {
        delete submissionData.emailForInvoicing;
      }

      onSubmit(submissionData);
    }
  };

  // Function to update all contact data
  const updateContactData = async () => {
    try {
      let currentContactId = contactId;

      if (!currentContactId) {
        if (contactData.contactId) {
          currentContactId = contactData.contactId;
        } else {
          const params = new URLSearchParams(window.location.search);
          const urlContactId = params.get("contactId");
          const hubspotUrl = params.get("hubspotUrl");

          if (urlContactId) {
            currentContactId = urlContactId;
          } else if (hubspotUrl) {
            const hubspotUrlPattern = /\/contacts\/\d+\/record\/0-1\/(\d+)/;
            const match = hubspotUrl.match(hubspotUrlPattern);
            if (match) {
              currentContactId = match[1];
            }
          }
        }
      }

      if (!currentContactId) {
        throw new Error("No contact ID available for update.");
      }

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      // Prepare properties to update - only send non-empty values
      const properties = {};
      if (contactData.firstname) properties.firstname = contactData.firstname;
      if (contactData.lastname) properties.lastname = contactData.lastname;
      if (contactData.company) properties.company = contactData.company;
      if (contactData.website) properties.website = contactData.website;
      if (contactData.email) properties.email = contactData.email;
      if (contactData.city) properties.city = contactData.city;
      if (contactData.state) properties.state = contactData.state;
      if (contactData.country) properties.country = contactData.country;

      console.log("DISABLED: Updating contact with properties:", properties);

      // DISABLED: Contact update API call
      /*
      const response = await fetch(
        `${apiUrl}/api/hubspot/contact/${currentContactId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "omit",
          mode: "cors",
          body: JSON.stringify({ properties }),
        }
      );
      */

      // Mock successful response
      const response = {
        ok: true,
        json: () => Promise.resolve({ success: true }),
      };

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! Status: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Contact update failed");
      }

      console.log("Successfully updated contact data");
    } catch (error) {
      console.error("Error updating contact data:", error);
      throw error;
    }
  };

  // Function to update contact information
  const updateContactInfo = async (field, value) => {
    try {
      // Get the correct contact ID
      let currentContactId = contactId;

      // If contactId prop is not available, try to extract from contactData or URL
      if (!currentContactId) {
        if (contactData.contactId) {
          currentContactId = contactData.contactId;
        } else {
          // Try to extract from URL parameters
          const params = new URLSearchParams(window.location.search);
          const urlContactId = params.get("contactId");
          const hubspotUrl = params.get("hubspotUrl");

          if (urlContactId) {
            currentContactId = urlContactId;
          } else if (hubspotUrl) {
            // Need to extract contact ID from HubSpot URL
            const hubspotUrlPattern = /\/contacts\/\d+\/record\/0-1\/(\d+)/;
            const match = hubspotUrl.match(hubspotUrlPattern);
            if (match) {
              currentContactId = match[1];
            }
          }
        }
      }

      // Validate that we have a contact ID
      if (!currentContactId) {
        throw new Error(
          "No contact ID available for update. Please ensure contact data is loaded."
        );
      }

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      // Use field names directly since they now match the API response
      const hubspotField = field;
      const properties = { [hubspotField]: value };

      console.log(
        `DISABLED: Updating contact ${currentContactId} field ${hubspotField} with value:`,
        value
      );

      // DISABLED: Contact update API call
      /*
      const response = await fetch(
        `${apiUrl}/api/hubspot/contact/${currentContactId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "omit",
          mode: "cors",
          body: JSON.stringify({ properties }),
        }
      );
      */

      // Mock successful response
      const response = {
        ok: true,
        json: () => Promise.resolve({ success: true }),
      };

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! Status: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();

      if (data.success) {
        // Update local state with new value
        setContactData((prev) => ({
          ...prev,
          [field]: value,
        }));
        console.log(`Successfully updated ${field} to:`, value);
      } else {
        throw new Error(data.message || "Update failed");
      }
    } catch (error) {
      console.error(`Error updating contact ${field}:`, error);
      throw error; // Re-throw to show error in UI
    }
  };

  // Helper function to get field error
  const getFieldError = (fieldName) => {
    return touched[fieldName] && errors[fieldName] ? errors[fieldName] : "";
  };

  // Helper function to handle contact field changes with validation
  const handleContactFieldChange = (fieldName, value) => {
    // Update contact data
    setContactData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Run validation for contact field
    const error = validateField(fieldName, value);
    setErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));

    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  // Progress calculation
  const getProgress = () => {
    const totalFields = Object.keys(formData).length - 1; // Exclude additionalComments
    const filledFields = Object.keys(formData).filter(
      (key) =>
        key !== "additionalComments" &&
        formData[key] &&
        formData[key].toString().trim()
    ).length;
    return Math.round((filledFields / totalFields) * 100);
  };

  const handleClose = () => {
    setFormData({
      projectTitle: "",
      engagementType: "",
      duration: "",
      firstPaymentAmount: "",
      amount: "",
      solutionsServices: "",
      pricingTerms: "",
      emailForContract: "",
      emailForInvoicing: "",
      numberOfResources: "",
      assignmentType: "",
      additionalComments: "",
    });
    setErrors({});
    setTouched({});
    onClose();
  };

  // Fetch deals for the contact
  const fetchDeals = async (id) => {
    setLoadingDeals(true);
    setDealError("");

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const response = await fetch(
        `${apiUrl}/api/hubspot/deals/contact/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "omit",
          mode: "cors",
        }
      );

      const data = await response.json();

      if (data.success) {
        setDeals(data.deals || []);
      } else {
        setDealError(data.message || "Failed to fetch deals");
      }
    } catch (err) {
      console.error("Error fetching deals:", err);
      setDealError("Error fetching deals: " + err.message);
    } finally {
      setLoadingDeals(false);
    }
  };

  // Handle deal selection
  const handleDealSelect = (dealId) => {
    setSelectedDealId(dealId);

    if (dealId === "new") {
      // Creating new deal - reset ALL fields to empty
      setSelectedDeal(null);

      // Find and set "Contract Sent" stage for new deals in Contract Modal
      const contractSentStage = dealStages.find(
        (stage) =>
          stage.id === "contractsent" || stage.label === "Contract Sent"
      );
      if (contractSentStage) {
        setDealStage(contractSentStage.id);
        console.log(
          "🎯 Contract Modal: New deal - stage set to Contract Sent:",
          contractSentStage.id
        );
      } else {
        setDealStage("contractsent"); // Fallback to static value
        console.warn(
          "⚠️ Contract Sent stage not found in dealStages, using fallback"
        );
      }

      // Reset ALL form fields to empty for new deal
      setFormData({
        projectTitle: "",
        engagementType: "",
        duration: "",
        firstPaymentAmount: "",
        amount: "",
        solutionsServices: "",
        pricingTerms: "",
        emailForContract: "",
        emailForInvoicing: "",
        numberOfResources: "",
        assignmentType: "",
        additionalComments: "",
      });

      // Keep contact data populated - do NOT clear client information section
    } else if (dealId) {
      // Editing existing deal - map HubSpot deal data to form fields
      const deal = deals.find((d) => d.id === dealId);
      if (deal) {
        setSelectedDeal(deal);

        // Map HubSpot deal data to existing form fields
        console.log("🔧 Mapping HubSpot deal data to form:", deal);
        setFormData((prevData) => ({
          ...prevData,
          // Map deal data to form fields using correct HubSpot property keys
          projectTitle: deal.properties?.dealname || "",
          solutionsServices: deal.properties?.services || "",
          engagementType: deal.properties?.engagement_type || "",
          duration: deal.properties?.contract_duration || "",
          amount: deal.amount?.toString() || deal.properties?.amount || "", // Use amount for total project cost
          firstPaymentAmount: deal.properties?.first_payment_amount || "", // Map first payment amount
          numberOfResources: deal.properties?.number_of_resources || "",
          assignmentType: deal.properties?.assignment_type || "",
          // Keep existing fields for email addresses
          emailForContract:
            deal.properties?.hs_email_for_contract || prevData.emailForContract,
          emailForInvoicing:
            deal.properties?.hs_email_for_invoicing ||
            prevData.emailForInvoicing,
          pricingTerms: deal.properties?.pricing_terms || prevData.pricingTerms,
        }));

        // Use the dealstage from properties, not the stage field
        setDealStage(
          deal.properties?.dealstage || deal.stage || "contractsent"
        );
        console.log(
          "🎯 Contract Modal: Selected deal stage from properties:",
          deal.properties?.dealstage || deal.stage
        );

        console.log("✅ Form pre-filled with HubSpot deal data");
      }
    } else {
      // No selection - reset to default state
      setSelectedDeal(null);

      // Find and set "Contract Sent" stage as default for Contract Modal
      const contractSentStage = dealStages.find(
        (stage) =>
          stage.id === "contractsent" || stage.label === "Contract Sent"
      );
      if (contractSentStage) {
        setDealStage(contractSentStage.id);
      } else {
        setDealStage("contractsent"); // Fallback to static value
      }
    }
  };

  // Deal stages state - will be populated from API
  const [dealStages, setDealStages] = useState([]);
  const [loadingDealStages, setLoadingDealStages] = useState(false);

  // Dynamic dropdown properties state
  const [dynamicProperties, setDynamicProperties] = useState({});
  const [loadingProperties, setLoadingProperties] = useState(false);

  // Fetch dynamic properties from HubSpot
  const fetchDynamicProperties = async () => {
    setLoadingProperties(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const keys =
        "engagement_type,contract_duration,number_of_resources,assignment_type,pricing_terms,experience_level,work_location,shift_timing";

      const response = await fetch(
        `${apiUrl}/api/hubspot/deal-properties?keys=${keys}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "omit",
          mode: "cors",
        }
      );

      const data = await response.json();
      if (data.success && data.properties) {
        // Convert array of properties to object with property names as keys
        const propertiesObj = {};
        data.properties.forEach((prop) => {
          propertiesObj[prop.name] = prop;
        });
        setDynamicProperties(propertiesObj);
        console.log(
          "✅ Dynamic properties loaded from HubSpot:",
          propertiesObj
        );
      } else {
        console.warn(
          "⚠️ Failed to load dynamic properties from HubSpot, using static fallback"
        );
      }
    } catch (error) {
      console.error("Error fetching dynamic properties:", error);
      // Keep static dropdown options if API fails
    } finally {
      setLoadingProperties(false);
    }
  };

  // Fetch deal stages from HubSpot
  const fetchDealStages = async () => {
    setLoadingDealStages(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const response = await fetch(`${apiUrl}/api/hubspot/deal-stages`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "omit",
        mode: "cors",
      });

      const data = await response.json();
      if (data.success && data.stages) {
        // Map the stages to the format expected by the UI
        const mappedStages = data.stages.map((stage) => ({
          id: stage.id || stage.value, // Use value field if id is not present
          label: stage.label,
        }));
        setDealStages(mappedStages);
        console.log("✅ Deal stages loaded from HubSpot:", mappedStages);
        console.log(
          "🔧 Available stage IDs:",
          mappedStages.map((s) => s.id)
        );

        // Set default stage for Contract Modal to "Contract Sent" if not already set
        if (!dealStage || dealStage === "contractsent") {
          const contractSentStage = mappedStages.find(
            (stage) =>
              stage.id === "contractsent" || stage.label === "Contract Sent"
          );
          if (contractSentStage) {
            setDealStage(contractSentStage.id);
            console.log(
              "🎯 Contract Modal: Default stage set to Contract Sent:",
              contractSentStage.id
            );
          } else {
            console.warn(
              "⚠️ Contract Sent stage not found, keeping current default"
            );
          }
        }
      } else {
        console.warn(
          "⚠️ Failed to load deal stages from HubSpot, using fallback"
        );
      }
    } catch (error) {
      console.error("Error fetching deal stages:", error);
      // Keep fallback stages if API fails
    } finally {
      setLoadingDealStages(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="contract-form-container">
      <div className="form-content">
        {showSuccess ? (
          <div className="success-state-clean">
            <div className="success-icon-clean">
              <Check size={64} />
            </div>
            <h3>Contract Request Submitted Successfully!</h3>
            <p className="success-message-clean">{successMessage}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="contract-form">
            {/* HubSpot Deal Selection Section */}
            {contactId && (
              <div className="form-section enhanced">
                <div className="section-header">
                  <div className="section-icon">
                    <TrendingUp size={20} />
                  </div>
                  <div>
                    <h3>HubSpot Deal</h3>
                    <p>Select an existing deal to edit or create a new one</p>
                  </div>
                </div>

                {dealError && (
                  <Alert severity="error" style={{ margin: "16px 0" }}>
                    {dealError}
                  </Alert>
                )}

                <div className="form-grid">
                  {/* Deal Selection Dropdown */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Select Deal <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <FormControl fullWidth required>
                      <InputLabel id="deal-select-label">
                        Choose a deal or create new
                      </InputLabel>
                      <Select
                        labelId="deal-select-label"
                        value={selectedDealId}
                        onChange={(e) => handleDealSelect(e.target.value)}
                        label="Choose a deal or create new"
                        size="small"
                        disabled={loadingDeals}
                      >
                        <MenuItem value="">
                          <em>Select an option...</em>
                        </MenuItem>
                        <MenuItem value="new">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <TrendingUp size={16} />
                            <strong>Create New Deal</strong>
                          </div>
                        </MenuItem>
                        {deals.map((deal) => (
                          <MenuItem key={deal.id} value={deal.id}>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>
                                {deal.name}
                              </span>
                              {/* <span
                                style={{ fontSize: "12px", color: "#6b7280" }}
                              >
                                {deal.formattedAmount} • {deal.stage}
                              </span> */}
                            </div>
                          </MenuItem>
                        ))}
                      </Select>
                      {loadingDeals && (
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Loader size={16} className="animate-spin" />
                          Loading deals...
                        </Typography>
                      )}
                    </FormControl>
                  </div>

                  {/* Deal Stage Selection */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Deal Stage <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel id="deal-stage-label">
                        Select Stage
                      </InputLabel>
                      <Select
                        labelId="deal-stage-label"
                        value={dealStage}
                        onChange={(e) => setDealStage(e.target.value)}
                        label="Select Stage"
                        size="small"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                            },
                          },
                        }}
                      >
                        {dealStages.map((stage) => (
                          <MenuItem key={stage.id} value={stage.id}>
                            {stage.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>
              </div>
            )}
            {/* Client Information Auto Fields Section */}
            {contactId && (
              <div className="form-section enhanced">
                <div className="section-header">
                  <div className="section-icon">
                    <User size={20} />
                  </div>
                  <div>
                    <h3>Client Information</h3>
                    <p>Auto-populated from HubSpot contact data</p>
                  </div>
                </div>

                {loadingContact ? (
                  <div
                    className="loading-section"
                    style={{ padding: "20px", textAlign: "center" }}
                  >
                    <Loader className="spin" size={24} />
                    <p>Loading client information...</p>
                  </div>
                ) : contactError ? (
                  <Alert severity="error" style={{ margin: "16px 0" }}>
                    {contactError}
                  </Alert>
                ) : (
                  <div className="form-grid">
                    {/* Client Name - Always Editable with TextField */}
                    <div className="form-field">
                      <Typography
                        variant="subtitle2"
                        component="label"
                        sx={{
                          mb: 1,
                          display: "block",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                      >
                        Client Name <span style={{ color: "#ef4444" }}>*</span>
                      </Typography>
                      <TextField
                        id="clientName"
                        name="clientName"
                        placeholder="Enter client name"
                        value={`${contactData.firstname || ""} ${
                          contactData.lastname || ""
                        }`.trim()}
                        onChange={(e) => {
                          const [firstname = "", ...lastNameParts] =
                            e.target.value.split(" ");
                          const lastname = lastNameParts.join(" ");
                          setContactData((prev) => ({
                            ...prev,
                            firstname,
                            lastname,
                          }));
                        }}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <User
                              size={18}
                              style={{ marginRight: "8px", color: "#6b7280" }}
                            />
                          ),
                        }}
                      />
                    </div>

                    {/* Company Name - Always Editable with TextField */}
                    <div className="form-field">
                      <Typography
                        variant="subtitle2"
                        component="label"
                        sx={{
                          mb: 1,
                          display: "block",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                      >
                        Company Name <span style={{ color: "#ef4444" }}>*</span>
                      </Typography>
                      <TextField
                        id="companyName"
                        name="companyName"
                        placeholder="Enter company name"
                        value={contactData.company || ""}
                        onChange={(e) => {
                          setContactData((prev) => ({
                            ...prev,
                            company: e.target.value,
                          }));
                        }}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <Building
                              size={18}
                              style={{ marginRight: "8px", color: "#6b7280" }}
                            />
                          ),
                        }}
                      />
                    </div>

                    {/* Company Website - Always Editable with TextField */}
                    <div className="form-field">
                      <Typography
                        variant="subtitle2"
                        component="label"
                        sx={{
                          mb: 1,
                          display: "block",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                      >
                        Company Website{" "}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </Typography>
                      <TextField
                        id="companyWebsite"
                        name="companyWebsite"
                        type="text"
                        placeholder="Enter company website (e.g., https://example.com)"
                        value={contactData.website || ""}
                        onChange={(e) => {
                          setContactData((prev) => ({
                            ...prev,
                            website: e.target.value,
                          }));
                        }}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <Globe
                              size={18}
                              style={{ marginRight: "8px", color: "#6b7280" }}
                            />
                          ),
                        }}
                      />
                    </div>

                    {/* Email - Always Editable with TextField */}
                    <div className="form-field">
                      <Typography
                        variant="subtitle2"
                        component="label"
                        sx={{
                          mb: 1,
                          display: "block",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                      >
                        Email Address{" "}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </Typography>
                      <TextField
                        id="emailAddress"
                        name="emailAddress"
                        type="email"
                        placeholder="Enter email address"
                        value={contactData.email || ""}
                        onChange={(e) => {
                          setContactData((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }));
                        }}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <Mail
                              size={18}
                              style={{ marginRight: "8px", color: "#6b7280" }}
                            />
                          ),
                        }}
                      />
                    </div>

                    {/* HubSpot Link - Auto Field */}
                    <div className="form-field">
                      <Typography
                        variant="subtitle2"
                        component="label"
                        sx={{
                          mb: 1,
                          display: "block",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                      >
                        HubSpot Contact Link (Auto-populated)
                      </Typography>
                      <TextField
                        id="hubspotLink"
                        name="hubspotLink"
                        placeholder="HubSpot Contact URL"
                        value={contactData.hubspotContactUrl}
                        disabled
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <Link
                              size={18}
                              style={{ marginRight: "8px", color: "#6b7280" }}
                            />
                          ),
                        }}
                      />
                    </div>

                    {/* Client Location - Always Editable with TextField */}
                    <div className="form-field">
                      <Typography
                        variant="subtitle2"
                        component="label"
                        sx={{
                          mb: 1,
                          display: "block",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                      >
                        Client Location{" "}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </Typography>
                      <TextField
                        id="clientLocation"
                        name="clientLocation"
                        placeholder="Enter location (City, State, Country)"
                        value={(() => {
                          const locationParts = [
                            contactData.city,
                            contactData.state,
                            contactData.country,
                          ].filter(Boolean);
                          return locationParts.length > 0
                            ? locationParts.join(", ")
                            : "";
                        })()}
                        onChange={(e) => {
                          // Parse location string and update individual fields
                          const parts = e.target.value
                            .split(",")
                            .map((part) => part.trim());

                          setContactData((prev) => ({
                            ...prev,
                            city: parts[0] || "",
                            state: parts[1] || "",
                            country: parts[2] || "",
                          }));
                        }}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <MapPin
                              size={18}
                              style={{ marginRight: "8px", color: "#6b7280" }}
                            />
                          ),
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Contact Details Section */}
            {
              <div className="form-section enhanced">
                <div className="section-header">
                  <div className="section-icon">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h3>Contact Details</h3>
                    <p>Specify the email addresses for contract and Invoice</p>
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-field">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        id="enableContractEmail"
                        checked={fieldEnabled.emailForContract}
                        onChange={(e) => {
                          setFieldEnabled((prev) => ({
                            ...prev,
                            emailForContract: e.target.checked,
                          }));
                          if (!e.target.checked) {
                            setFormData((prev) => ({
                              ...prev,
                              emailForContract: "",
                            }));
                          }
                        }}
                        style={{ marginRight: "8px" }}
                      />
                      <Typography
                        variant="subtitle2"
                        component="label"
                        htmlFor="enableContractEmail"
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          cursor: "pointer",
                        }}
                      >
                        Contract <span style={{ color: "#ef4444" }}>*</span>
                      </Typography>
                    </div>
                    <TextField
                      id="emailForContract"
                      name="emailForContract"
                      type="email"
                      placeholder="Enter Contract Email"
                      value={formData.emailForContract}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!getFieldError("emailForContract")}
                      helperText={
                        getFieldError("emailForContract") ||
                        (formData.emailForContract
                          ? "Pre-filled from contact data"
                          : "")
                      }
                      required={fieldEnabled.emailForContract}
                      disabled={!fieldEnabled.emailForContract}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <User
                            size={18}
                            style={{ marginRight: "8px", color: "#6b7280" }}
                          />
                        ),
                      }}
                    />
                  </div>

                  <div className="form-field">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        id="enableInvoicingEmail"
                        checked={fieldEnabled.emailForInvoicing}
                        onChange={(e) => {
                          setFieldEnabled((prev) => ({
                            ...prev,
                            emailForInvoicing: e.target.checked,
                          }));
                          if (!e.target.checked) {
                            setFormData((prev) => ({
                              ...prev,
                              emailForInvoicing: "",
                            }));
                          }
                        }}
                        style={{ marginRight: "8px" }}
                      />
                      <Typography
                        variant="subtitle2"
                        component="label"
                        htmlFor="enableInvoicingEmail"
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          cursor: "pointer",
                        }}
                      >
                        Invoice <span style={{ color: "#ef4444" }}>*</span>
                      </Typography>
                    </div>
                    <TextField
                      id="emailForInvoicing"
                      name="emailForInvoicing"
                      type="email"
                      placeholder="Enter Invoice Email"
                      value={formData.emailForInvoicing}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!getFieldError("emailForInvoicing")}
                      helperText={
                        getFieldError("emailForInvoicing") ||
                        (formData.emailForInvoicing
                          ? "Pre-filled from contact data"
                          : "")
                      }
                      required={fieldEnabled.emailForInvoicing}
                      disabled={!fieldEnabled.emailForInvoicing}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <Building
                            size={18}
                            style={{ marginRight: "8px", color: "#6b7280" }}
                          />
                        ),
                      }}
                    />
                  </div>
                </div>
              </div>
            }

            {/* Project and Engagement Details Section */}
            {
              <div className="form-section enhanced">
                <div className="section-header">
                  <div className="section-icon">
                    <Briefcase size={20} />
                  </div>
                  <div>
                    <h3>Project and Engagement Details</h3>
                    <p>Define your project scope and engagement terms</p>
                  </div>
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Project Title <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <TextField
                    id="projectTitle"
                    name="projectTitle"
                    placeholder="Enter your project title (company name will be added automatically)"
                    value={formData.projectTitle}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!getFieldError("projectTitle")}
                    helperText={
                      getFieldError("projectTitle") ||
                      `Project title will automatically include " - ${
                        contactData.company || "Company Name"
                      }" as suffix`
                    }
                    required
                    fullWidth
                    size="small"
                  />
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Solutions & Services{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <TextField
                    id="solutionsServices"
                    name="solutionsServices"
                    placeholder="Describe the solutions and services needed (e.g., Web Design, SEO, Marketing Automation)"
                    value={formData.solutionsServices}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!getFieldError("solutionsServices")}
                    helperText={
                      getFieldError("solutionsServices") ||
                      (formData.solutionsServices ? "" : "")
                    }
                    required
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    className="multiline"
                  />
                </div>

                <div className="form-grid">
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Engagement Type{" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <FormControl
                      fullWidth
                      required
                      error={!!getFieldError("engagementType")}
                    >
                      <InputLabel id="engagementType-label">
                        Select Engagement Type
                      </InputLabel>
                      <Select
                        labelId="engagementType-label"
                        id="engagementType"
                        name="engagementType"
                        value={formData.engagementType}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Select Engagement Type"
                        size="small"
                      >
                        <MenuItem value="">
                          Choose your preferred engagement model
                        </MenuItem>
                        {/* Only dynamic options from HubSpot API */}
                        {dynamicProperties.engagement_type?.options?.map(
                          (option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          )
                        )}
                      </Select>
                      {getFieldError("engagementType") && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, ml: 1.5 }}
                        >
                          {getFieldError("engagementType")}
                        </Typography>
                      )}
                    </FormControl>
                  </div>

                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Contract Duration{" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <FormControl
                      fullWidth
                      required
                      error={!!getFieldError("duration")}
                    >
                      <InputLabel id="duration-label">
                        Select Duration
                      </InputLabel>
                      <Select
                        labelId="duration-label"
                        id="duration"
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Select Contract Duration"
                        size="small"
                      >
                        <MenuItem value="">Choose project timeline</MenuItem>
                        {/* Only dynamic options from HubSpot API */}
                        {dynamicProperties.contract_duration?.options?.map(
                          (option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          )
                        )}
                      </Select>
                      {getFieldError("duration") && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, ml: 1.5 }}
                        >
                          {getFieldError("duration")}
                        </Typography>
                      )}
                    </FormControl>
                  </div>

                  {/* Number of Resources */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Number of Resources{" "}
                      {formData.engagementType !== "Project" && (
                        <span style={{ color: "#ef4444" }}>*</span>
                      )}
                    </Typography>
                    <FormControl
                      fullWidth
                      disabled={formData.engagementType === "Project"}
                      error={!!getFieldError("numberOfResources")}
                    >
                      <InputLabel id="numberOfResources-label">
                        Select Number of Resources
                      </InputLabel>
                      <Select
                        labelId="numberOfResources-label"
                        id="numberOfResources"
                        name="numberOfResources"
                        value={formData.numberOfResources}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Select Number of Resources"
                        size="small"
                        disabled={formData.engagementType === "Project"}
                      >
                        <MenuItem value="">Choose number of resources</MenuItem>
                        {/* Only dynamic options from HubSpot API */}
                        {dynamicProperties.number_of_resources?.options?.map(
                          (option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          )
                        )}
                      </Select>
                      {getFieldError("numberOfResources") && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, ml: 1.5 }}
                        >
                          {getFieldError("numberOfResources")}
                        </Typography>
                      )}
                    </FormControl>
                  </div>

                  {/* Assignment Type */}
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Assignment Type{" "}
                      {formData.engagementType !== "Project" && (
                        <span style={{ color: "#ef4444" }}>*</span>
                      )}
                    </Typography>
                    <FormControl
                      fullWidth
                      disabled={formData.engagementType === "Project"}
                      error={!!getFieldError("assignmentType")}
                    >
                      <InputLabel id="assignmentType-label">
                        Select Assignment Type
                      </InputLabel>
                      <Select
                        labelId="assignmentType-label"
                        id="assignmentType"
                        name="assignmentType"
                        value={formData.assignmentType}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Select Assignment Type"
                        size="small"
                        disabled={formData.engagementType === "Project"}
                      >
                        <MenuItem value="">Choose assignment type</MenuItem>
                        {/* Only dynamic options from HubSpot API */}
                        {dynamicProperties.assignment_type?.options?.map(
                          (option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          )
                        )}
                      </Select>
                      {getFieldError("assignmentType") && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, ml: 1.5 }}
                        >
                          {getFieldError("assignmentType")}
                        </Typography>
                      )}
                    </FormControl>
                  </div>
                </div>
              </div>
            }

            {/* Pricing Information Section */}
            {
              <div className="form-section enhanced">
                <div className="section-header">
                  <div className="section-icon">
                    <DollarSign size={20} />
                  </div>
                  <div>
                    <h3>Pricing Information</h3>
                    <p>Define the financial terms and pricing structure</p>
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      First Payment <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      id="firstPaymentAmount"
                      name="firstPaymentAmount"
                      type="number"
                      placeholder="Enter first payment amount (e.g., 3500)"
                      value={formData.firstPaymentAmount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!getFieldError("firstPaymentAmount")}
                      helperText={
                        getFieldError("firstPaymentAmount") ||
                        "Minimum amount: $100"
                      }
                      required
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <DollarSign
                            size={18}
                            style={{ marginRight: "8px", color: "#6b7280" }}
                          />
                        ),
                      }}
                    />
                  </div>

                  <div className="form-field">
                    <Typography
                      variant="subtitle2"
                      component="label"
                      sx={{
                        mb: 1,
                        display: "block",
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Total Project Cost (USD){" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </Typography>
                    <TextField
                      id="amount"
                      name="amount"
                      type="number"
                      placeholder="Enter total project cost (e.g., 7000)"
                      value={formData.amount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!getFieldError("amount")}
                      helperText={getFieldError("amount")}
                      required
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <DollarSign
                            size={18}
                            style={{ marginRight: "8px", color: "#6b7280" }}
                          />
                        ),
                      }}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <Typography
                    variant="subtitle2"
                    component="label"
                    sx={{
                      mb: 1,
                      display: "block",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    Payment Terms <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <FormControl
                    fullWidth
                    required
                    error={!!getFieldError("pricingTerms")}
                  >
                    <InputLabel id="pricingTerms-label">
                      Select Payment Terms
                    </InputLabel>
                    <Select
                      labelId="pricingTerms-label"
                      id="pricingTerms"
                      name="pricingTerms"
                      value={formData.pricingTerms}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Select Payment Terms"
                      size="small"
                    >
                      <MenuItem value="">Choose your payment terms</MenuItem>
                      {/* Only dynamic options from HubSpot API */}
                      {dynamicProperties.pricing_terms?.options?.map(
                        (option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        )
                      )}
                    </Select>
                    {getFieldError("pricingTerms") && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.5 }}
                      >
                        {getFieldError("pricingTerms")}
                      </Typography>
                    )}
                  </FormControl>

                  {/* Custom Payment Terms Field - Show when "Custom Payment Terms" is selected */}
                  {formData.pricingTerms === "Custom Payment Terms" && (
                    <div style={{ marginTop: "16px" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          display: "block",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                      >
                        Custom Payment Terms{" "}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        id="customPricingValue"
                        name="customPricingValue"
                        value={formData.customPricingValue}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!getFieldError("customPricingValue")}
                        helperText={getFieldError("customPricingValue")}
                        size="small"
                        required
                        placeholder="e.g., 30% upfront, 70% on completion"
                      />
                    </div>
                  )}
                </div>
              </div>
            }

            {/* Additional Comments Section */}
            <div className="form-section enhanced">
              <div className="section-header">
                <div className="section-icon">
                  <MessageCircle size={20} />
                </div>
                <div>
                  <h3>Additional Comments</h3>
                  <p>
                    Any additional information or special requirements
                    (Optional)
                  </p>
                </div>
              </div>

              <div className="form-field">
                <Typography
                  variant="subtitle2"
                  component="label"
                  sx={{
                    mb: 1,
                    display: "block",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  Additional Comments (Optional)
                </Typography>
                <TextField
                  id="additionalComments"
                  name="additionalComments"
                  placeholder="Enter any additional information, special requirements, or notes about this project..."
                  value={formData.additionalComments}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  className="multiline"
                  helperText="This field is optional. Use it to provide any extra context or requirements."
                />
              </div>
            </div>

            {/* Compact Missing Fields Summary - Always visible if there are missing fields */}
            {(() => {
              const summary = getValidationSummary();
              return (
                summary.missing.length > 0 && (
                  <div
                    style={{
                      background: "#fee2e2",
                      border: "1px solid #fca5a5",
                      borderRadius: "6px",
                      padding: "12px",
                      margin: "16px 0",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#dc2626",
                        fontWeight: 600,
                        marginBottom: "6px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <FileText size={16} />
                      Please complete {summary.missing.length} missing required
                      field{summary.missing.length > 1 ? "s" : ""}:
                    </Typography>

                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                    >
                      {summary.missing.map((field, index) => (
                        <span
                          key={index}
                          style={{
                            backgroundColor: "#dc2626",
                            color: "white",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              );
            })()}

            {/* Expanded Validation Summary */}
            {showValidationSummary && (
              <div
                style={{
                  background: "#fff3cd",
                  border: "1px solid #ffeaa7",
                  borderRadius: "8px",
                  padding: "16px",
                  margin: "16px 0",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#856404",
                    fontWeight: 600,
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FileText size={20} />
                  Form Completion Status
                </Typography>

                {(() => {
                  const summary = getValidationSummary();
                  return (
                    <div>
                      <Typography
                        variant="body2"
                        sx={{ color: "#856404", marginBottom: "8px" }}
                      >
                        <strong>Progress:</strong> {summary.filled} of{" "}
                        {summary.total} required fields completed
                      </Typography>

                      {summary.missing.length > 0 && (
                        <div style={{ marginBottom: "12px" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#d63384",
                              fontWeight: 600,
                              marginBottom: "4px",
                            }}
                          >
                            Missing Required Fields:
                          </Typography>
                          <ul
                            style={{
                              margin: "0",
                              paddingLeft: "20px",
                              color: "#d63384",
                            }}
                          >
                            {summary.missing.map((field, index) => (
                              <li key={index} style={{ fontSize: "14px" }}>
                                {field}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {summary.completed.length > 0 && (
                        <div>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#198754",
                              fontWeight: 600,
                              marginBottom: "4px",
                            }}
                          >
                            Completed Fields:
                          </Typography>
                          <ul
                            style={{
                              margin: "0",
                              paddingLeft: "20px",
                              color: "#198754",
                            }}
                          >
                            {summary.completed.map((field, index) => (
                              <li key={index} style={{ fontSize: "14px" }}>
                                {field}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div style={{ marginTop: "12px" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowValidationSummary(false)}
                          sx={{
                            color: "#856404",
                            borderColor: "#856404",
                            "&:hover": {
                              borderColor: "#856404",
                              backgroundColor: "rgba(133, 100, 4, 0.04)",
                            },
                          }}
                        >
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Footer */}
            {
              <div className="form-footer">
                <button
                  type="submit"
                  className={`form-submit-btn ${
                    loading || !isFormValid ? "disabled" : ""
                  }`}
                  disabled={loading || !isFormValid}
                >
                  <div className="btn-shine" />
                  <div className="btn-content">
                    {loading ? (
                      <>
                        <Loader className="btn-spinner" size={20} />
                        <span>Submitting Contract Request...</span>
                      </>
                    ) : (
                      <>
                        <Send size={20} className="btn-icon" />
                        <span>
                          {selectedDeal ? "Send Request" : "Send Request"}
                        </span>
                      </>
                    )}
                  </div>
                </button>
              </div>
            }
          </form>
        )}
      </div>
    </div>
  );
};

export default ContractModal;
